import { useState, useEffect, useCallback, useMemo } from "react";
import { useHref } from "react-router-dom";
import { Button, cn, HoverCard, HoverCardTrigger, HoverCardContent, PortalContainerContext } from "@tellescope/ui";
import { Card } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { RefreshCw, ExternalLink, Loader2, Columns2, Maximize2, Smartphone } from "lucide-react";
import { useEnduserIsAuthenticated, useEnduserCurrentEnduser, useEnduserLogout } from "../../enduser/store/hooks";
import { useAuth } from "@tellescope/tanstack-query";
import { useOrganization } from "../../shared/store/organizationStore";
import { EnduserQueryProvider } from "../../enduser/components/EnduserQueryProvider";
import { invalidateEnduserQueries } from "../../enduser/components/enduserQueryClient";
import { VideoCallingProvider } from "../../enduser/providers/VideoCallingProvider";
import { useThemeStyles } from "../../shared/hooks/useThemeStyles";
import { useFontLoader } from "../../shared/hooks/useFontLoader";
import { PortalSchemaProvider } from "../../shared/context/PortalSchemaContext";
import { CopyableField } from "./CopyableField";
import { PreviewPortalContent } from "./PreviewPortalContent";
import type { PortalSchema } from "../../shared/schema";
import type { PreviewViewportMode } from "../pages/DashboardPage";

interface PortalPreviewProps {
  schema: PortalSchema;
  onSelectEnduser: () => void;
  /**
   * Optional: When provided, the preview will sync to this page when it changes.
   * Used by admin view to sync page selection with the preview.
   * When not provided (patient-facing view), the preview manages its own navigation.
   */
  selectedPageId?: string | null;
  /** Current viewport mode for the preview */
  viewportMode?: PreviewViewportMode;
  /** Callback to change the viewport mode */
  onViewportModeChange?: (mode: PreviewViewportMode) => void;
  /** Origin to use for the "View Live" link. Defaults to window.location.origin (the default portal). */
  livePortalOrigin?: string;
}

export const PortalPreview: React.FC<PortalPreviewProps> = ({
  schema,
  onSelectEnduser,
  selectedPageId,
  viewportMode,
  onViewportModeChange,
  livePortalOrigin,
}) => {
  const { t } = useTranslation();
  const isAuthenticated = useEnduserIsAuthenticated();
  const currentEnduser = useEnduserCurrentEnduser();
  const logout = useEnduserLogout();
  const { session } = useAuth();
  const organization = useOrganization();
  // Resolves "/" through the router's basename so base-path configs are respected
  const routerRoot = useHref("/");
  const [currentPageId, setCurrentPageId] = useState<string | null>(schema.pages[0]?.id ?? null);

  // Theme styles are applied as inline styles on the preview wrapper div (scoped),
  // rather than on document.documentElement, to prevent portal theme vars from
  // bleeding into the admin dashboard UI. Radix portals (Dialog, Sheet) opened
  // from within the preview are redirected into portalContainer via PortalContainerContext
  // so they also inherit the scoped CSS vars. See OrganizationThemeProvider.tsx.
  const themeStyles = useThemeStyles(schema.settings?.theming);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  // Load custom fonts for the portal preview (global font, heading font, body font)
  useFontLoader(
    schema.settings?.theming?.fontFamily,
    schema.settings?.theming?.customFontUrl,
    schema.settings?.theming?.headingFont,
    schema.settings?.theming?.bodyFont
  );

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isOpeningLivePortal, setIsOpeningLivePortal] = useState(false);

  const handleOpenLivePortal = useCallback(async () => {
    if (!session || !currentEnduser) return;

    setIsOpeningLivePortal(true);

    try {
      // Generate a fresh auth token for the current enduser
      const tokenResult = await session.api.endusers.generate_auth_token({
        id: currentEnduser.id,
        overrideConsent: true,
        overrideOTP: true,
      });

      // Determine the page path - use router root for first page, otherwise append slug(s).
      // routerRoot (from useHref("/")) respects the router's configured basename.
      const firstPage = schema.pages[0];
      const activeTopLevelPage = schema.pages.find((p) => p.id === currentPageId);
      const parentOfActiveChild = schema.pages.find((p) => p.pages?.some((c) => c.id === currentPageId));
      const activeChildPage = parentOfActiveChild?.pages?.find((c) => c.id === currentPageId);

      let pagePath = routerRoot;
      if (activeChildPage && parentOfActiveChild) {
        // The parent slug is always included, even for the first page (whose own path is routerRoot), since
        // "{routerRoot}{childSlug}" would be parsed as an unknown top-level slug and redirected home
        pagePath = `${routerRoot}${parentOfActiveChild.slug}/${activeChildPage.slug}`;
      } else if (activeTopLevelPage && activeTopLevelPage.slug !== firstPage?.slug) {
        pagePath = `${routerRoot}${activeTopLevelPage.slug}`;
      }

      // Build the live portal URL with the current page path
      const origin = livePortalOrigin ?? window.location.origin;
      const livePortalURL = `${origin}${pagePath}?token=${encodeURIComponent(tokenResult.authToken)}`;

      // Log URL for debugging - allows easy copy/paste to test token behavior
      // console.log("[View Live] Opening portal URL:", livePortalURL);

      // Open in new tab
      window.open(livePortalURL, "_blank");
    } catch (err) {
      console.error("Failed to open live portal:", err);
    } finally {
      setIsOpeningLivePortal(false);
    }
  }, [session, currentEnduser, schema.pages, currentPageId, livePortalOrigin, routerRoot]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // Run invalidation and minimum delay in parallel, wait for both
      await Promise.all([
        invalidateEnduserQueries(),
        new Promise((resolve) => setTimeout(resolve, 500)), // Minimum 500ms animation
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Sync with external selectedPageId when provided (admin view)
  // This allows the admin to control which page is shown while still allowing
  // the preview to navigate independently via the navbar
  useEffect(() => {
    if (selectedPageId !== undefined && selectedPageId !== currentPageId) {
      setCurrentPageId(selectedPageId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally excluding currentPageId to allow one-way sync from admin
  }, [selectedPageId]);

  // Find current page from schema - may be a top-level page or a nested sub-page
  const currentPage = useMemo(() => {
    for (const page of schema.pages) {
      if (page.id === currentPageId) return page;
      const child = page.pages.find((c) => c.id === currentPageId);
      if (child) return child;
    }
    return schema.pages[0] ?? null;
  }, [schema.pages, currentPageId]);

  // If no enduser session, show selector prompt
  if (!isAuthenticated) {
    return (
      <div className="h-full flex flex-col">
        {/* Header matching the authenticated state */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-muted border-b">
          <div className="text-sm">
            <span className="text-muted-foreground">{t("Portal Preview")}</span>
          </div>
          <Button variant="outline" size="sm" onClick={onSelectEnduser}>
            {t("Select Enduser")}
          </Button>
        </div>

        {/* Prompt content */}
        <div className="flex-1 flex items-center justify-center bg-muted/30 p-8">
          <Card variant="floating" padding="root" className="w-full max-w-md">
            <Card.Header className="text-center">
              <Card.Title className="text-xl">{t("No Enduser Selected")}</Card.Title>
            </Card.Header>
            <Card.Body className="space-y-4 text-center">
              <p className="text-muted-foreground">{t("Select an enduser to preview the portal as them.")}</p>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  // Render the actual enduser portal
  return (
    <EnduserQueryProvider>
      <VideoCallingProvider>
        <div className="h-full flex flex-col">
          {/* Preview header with enduser info */}
          <div className="flex items-center justify-between px-4 py-2.5 bg-muted border-b">
            <div className="text-sm">
              <span className="text-muted-foreground">{t("Viewing as")}:</span>{" "}
              <HoverCard openDelay={200} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <span className="font-medium cursor-help">
                    {currentEnduser?.fname || currentEnduser?.lname
                      ? `${currentEnduser?.fname ?? ""} ${currentEnduser?.lname ?? ""}`.trim()
                      : currentEnduser?.email ?? t("Unknown")}
                  </span>
                </HoverCardTrigger>
                <HoverCardContent className="w-72" align="start">
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold mb-2">{t("Enduser Details")}</h4>
                    {currentEnduser?.id && <CopyableField label={t("ID")} value={currentEnduser.id} />}
                    {currentEnduser?.email && <CopyableField label={t("Email")} value={currentEnduser.email} />}
                    {currentEnduser?.phone && <CopyableField label={t("Phone")} value={currentEnduser.phone} />}
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
            <div className="flex items-center gap-2">
              {/* Viewport mode toggle - ordered from widest to narrowest */}
              {onViewportModeChange && (
                <div className="flex items-center border rounded-md bg-muted/50">
                  <Button
                    variant={viewportMode === "fullscreen" ? "default" : "ghost"}
                    size="sm"
                    className={cn("h-7 px-2 rounded-r-none", viewportMode !== "fullscreen" && "hover:bg-muted")}
                    onClick={() => onViewportModeChange("fullscreen")}
                    title={t("Fullscreen preview")}
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewportMode === "split" ? "default" : "ghost"}
                    size="sm"
                    className={cn("h-7 px-2 rounded-none border-x", viewportMode !== "split" && "hover:bg-muted")}
                    onClick={() => onViewportModeChange("split")}
                    title={t("50/50 split view")}
                  >
                    <Columns2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewportMode === "mobile" ? "default" : "ghost"}
                    size="sm"
                    className={cn("h-7 px-2 rounded-l-none", viewportMode !== "mobile" && "hover:bg-muted")}
                    onClick={() => onViewportModeChange("mobile")}
                    title={t("Mobile preview")}
                  >
                    <Smartphone className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleOpenLivePortal}
                disabled={isOpeningLivePortal}
                title={t("Open live portal in new tab")}
              >
                {isOpeningLivePortal ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4" />
                )}
                <span className="ml-1">{t("View Live")}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title={t("Refresh data")}
              >
                <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
              </Button>
              <Button variant="outline" size="sm" onClick={onSelectEnduser}>
                {t("Change")}
              </Button>
            </div>
          </div>

          {/* PortalContainerContext scopes Radix portals (Dialog, Sheet) to this preview
              pane so they inherit the portal theme CSS vars rather than escaping to
              document.body and losing the scoped styling. */}
          <PortalContainerContext.Provider value={portalContainer}>
            {/* Scoped theme wrapper: portal CSS vars are applied here and cascade to all
                preview content. Kept separate from the admin chrome above so theme vars
                don't affect the preview header, viewport toggles, or other admin UI. */}
            <div style={themeStyles} className="flex-1 flex flex-col overflow-hidden">
              <PortalSchemaProvider schema={schema}>
                <PreviewPortalContent
                  schema={schema}
                  currentPage={currentPage}
                  onNavigate={setCurrentPageId}
                  onLogout={logout}
                  logoURL={organization?.logoURL}
                  organizationName={organization?.name}
                  showSidebar={schema.settings?.layout?.enableSidebarNavigation ?? false}
                  emptyMessage={t("No blocks yet. Add blocks from the editor to see them here.")}
                />
              </PortalSchemaProvider>
              {/* Portal outlet: Radix Dialog/Sheet content mounts here (inside the scoped
                  theme wrapper) instead of document.body, inheriting the portal theme vars. */}
              <div ref={setPortalContainer} />
            </div>
          </PortalContainerContext.Provider>
        </div>
      </VideoCallingProvider>
    </EnduserQueryProvider>
  );
};
